/**
 * Secure Storage Utility for API Keys
 *
 * Encrypts sensitive data before storing in localStorage
 * Uses Web Crypto API (browser native, no dependencies)
 *
 * Security Features:
 * - AES-GCM encryption
 * - User-specific encryption key (derived from user ID)
 * - No plain-text API keys in localStorage
 * - Protection against XSS key theft
 *
 * Usage:
 *   import secureStorage from './utils/secureStorage';
 *   await secureStorage.setItem('api_key', 'secret-key-123', userId);
 *   const key = await secureStorage.getItem('api_key', userId);
 */

import logger from './logger';

class SecureStorage {
  constructor() {
    this.algorithm = 'AES-GCM';
    this.keyLength = 256;
  }

  /**
   * Derive encryption key from user ID
   * @private
   */
  async deriveKey(userId) {
    // Use a consistent salt (in production, this could be per-user from backend)
    const salt = 'ekamanam-secure-storage-v1';
    const keyMaterial = `${userId}-${salt}`;

    // Convert to ArrayBuffer
    const encoder = new TextEncoder();
    const keyData = encoder.encode(keyMaterial);

    // Import as raw key material
    const importedKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive actual encryption key
    const derivedKey = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: encoder.encode(salt),
        iterations: 100000,
        hash: 'SHA-256'
      },
      importedKey,
      { name: this.algorithm, length: this.keyLength },
      false,
      ['encrypt', 'decrypt']
    );

    return derivedKey;
  }

  /**
   * Encrypt and store data
   * @param {string} key - Storage key name
   * @param {string} value - Value to encrypt and store
   * @param {string} userId - User ID for encryption key derivation
   */
  async setItem(key, value, userId) {
    if (!value || !userId) {
      logger.warn('SecureStorage: Missing value or userId');
      return;
    }

    try {
      // Generate encryption key
      const encryptionKey = await this.deriveKey(userId);

      // Generate random IV (Initialization Vector)
      const iv = crypto.getRandomValues(new Uint8Array(12));

      // Encrypt the value
      const encoder = new TextEncoder();
      const encodedValue = encoder.encode(value);

      const encryptedData = await crypto.subtle.encrypt(
        {
          name: this.algorithm,
          iv: iv
        },
        encryptionKey,
        encodedValue
      );

      // Store encrypted data with IV
      const encryptedArray = new Uint8Array(encryptedData);
      const combinedArray = new Uint8Array(iv.length + encryptedArray.length);
      combinedArray.set(iv, 0);
      combinedArray.set(encryptedArray, iv.length);

      // Convert to base64 for storage
      const base64Data = btoa(String.fromCharCode(...combinedArray));

      // Store in localStorage with prefix
      localStorage.setItem(`secure_${key}`, base64Data);

      logger.debug(`SecureStorage: Encrypted and stored ${key}`);
    } catch (error) {
      logger.error('SecureStorage: Encryption failed', error);
      throw error;
    }
  }

  /**
   * Retrieve and decrypt data
   * @param {string} key - Storage key name
   * @param {string} userId - User ID for decryption key derivation
   * @returns {Promise<string|null>} Decrypted value or null
   */
  async getItem(key, userId) {
    if (!userId) {
      logger.warn('SecureStorage: Missing userId for decryption');
      return null;
    }

    try {
      // Retrieve encrypted data
      const base64Data = localStorage.getItem(`secure_${key}`);
      if (!base64Data) {
        return null;
      }

      // Convert from base64
      const combinedArray = new Uint8Array(
        atob(base64Data).split('').map(char => char.charCodeAt(0))
      );

      // Extract IV and encrypted data
      const iv = combinedArray.slice(0, 12);
      const encryptedData = combinedArray.slice(12);

      // Generate decryption key
      const decryptionKey = await this.deriveKey(userId);

      // Decrypt
      const decryptedData = await crypto.subtle.decrypt(
        {
          name: this.algorithm,
          iv: iv
        },
        decryptionKey,
        encryptedData
      );

      // Convert back to string
      const decoder = new TextDecoder();
      const decryptedValue = decoder.decode(decryptedData);

      logger.debug(`SecureStorage: Decrypted ${key}`);
      return decryptedValue;
    } catch (error) {
      logger.error('SecureStorage: Decryption failed', error);
      // If decryption fails, might be corrupted or wrong user
      return null;
    }
  }

  /**
   * Remove item from storage
   * @param {string} key - Storage key name
   */
  removeItem(key) {
    localStorage.removeItem(`secure_${key}`);
    logger.debug(`SecureStorage: Removed ${key}`);
  }

  /**
   * Check if item exists
   * @param {string} key - Storage key name
   * @returns {boolean}
   */
  hasItem(key) {
    return localStorage.getItem(`secure_${key}`) !== null;
  }

  /**
   * Migrate existing plain-text keys to encrypted storage
   * IMPORTANT: Call this once during user login to migrate existing keys
   * @param {string} userId - User ID
   */
  async migrateExistingKeys(userId) {
    const keysToMigrate = [
      { old: 'gemini_pat', new: 'gemini_api_key' },
      { old: 'groq_api_key', new: 'groq_api_key' },
      { old: 'perplexity_api_key', new: 'perplexity_api_key' },
      { old: 'claude_api_key', new: 'claude_api_key' }
    ];

    for (const { old, new: newKey } of keysToMigrate) {
      const oldValue = localStorage.getItem(old);
      if (oldValue && oldValue.trim()) {
        try {
          // Encrypt and store with new key
          await this.setItem(newKey, oldValue, userId);
          // Remove old plain-text key
          localStorage.removeItem(old);
          logger.info(`SecureStorage: Migrated ${old} to encrypted storage`);
        } catch (error) {
          logger.error(`SecureStorage: Failed to migrate ${old}`, error);
        }
      }
    }
  }

  /**
   * Clear all secure storage (logout)
   */
  clearAll() {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('secure_')) {
        localStorage.removeItem(key);
      }
    });
    logger.info('SecureStorage: Cleared all encrypted data');
  }
}

// Export singleton instance
const secureStorage = new SecureStorage();

export default secureStorage;
